import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  getStop,
  listReachableStops,
  listStops,
  listStopsByRoute,
  type ListStopsParams,
} from '@/lib/api/endpoints';
import type {
  ItemEnvelope,
  ListEnvelope,
  ReachableStopRead,
  StopRead,
  StopsByRouteRead,
} from '@/types';

const HOUR = 60 * 60 * 1000;

export const stopsKeys = {
  all: ['stops'] as const,
  list: (params?: ListStopsParams) => ['stops', 'list', params ?? {}] as const,
  byRoute: (ids: string[]) => ['stops', 'by-route', ids] as const,
  reachable: (routeId: string, fromStopId: string) =>
    ['stops', 'reachable', routeId, fromStopId] as const,
  detail: (id: string) => ['stops', 'detail', id] as const,
};

export function useStops(params?: ListStopsParams): UseQueryResult<ListEnvelope<StopRead>> {
  return useQuery({
    queryKey: stopsKeys.list(params),
    queryFn: ({ signal }) => listStops(params ?? {}, signal),
    staleTime: HOUR,
  });
}

/**
 * Fetch stops for many routes in one request, each tagged with the routes
 * serving it. Replaces the map's per-route `useStops` fan-out. The query key
 * sorts the ids so it stays cache-stable regardless of the caller's ordering.
 */
export function useStopsByRoute(routeIds: string[]): UseQueryResult<StopsByRouteRead> {
  const ids = [...routeIds].sort();
  return useQuery({
    queryKey: stopsKeys.byRoute(ids),
    queryFn: async ({ signal }): Promise<StopsByRouteRead> => {
      const env = await listStopsByRoute(ids, signal);
      return env.data;
    },
    enabled: ids.length > 0,
    staleTime: HOUR,
  });
}

/**
 * Stops reachable from an origin on a route, each tagged with the direction
 * to ride. Drives the save flow's destination picker; disabled until both a
 * route and an origin stop are known.
 */
export function useReachableStops(
  routeId: string | undefined,
  fromStopId: string | undefined,
): UseQueryResult<ListEnvelope<ReachableStopRead>> {
  return useQuery({
    queryKey: stopsKeys.reachable(routeId ?? '', fromStopId ?? ''),
    queryFn: ({ signal }) => listReachableStops(routeId ?? '', fromStopId ?? '', signal),
    enabled: Boolean(routeId) && Boolean(fromStopId),
    staleTime: HOUR,
  });
}

export function useStop(id: string | undefined): UseQueryResult<ItemEnvelope<StopRead>> {
  return useQuery({
    queryKey: stopsKeys.detail(id ?? ''),
    queryFn: ({ signal }) => getStop(id ?? '', signal),
    enabled: Boolean(id),
    staleTime: HOUR,
  });
}
